import React, { useMemo } from 'react';
import { FilterMenuDualPanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterMenuDualPanelItemProps } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineNonDefaultSecondaryFilterCount } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineSecondaryFilterDisplayName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineSecondaryFilterInvalidKeys } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { SecondaryFilterContent } from 'apps/sigma2-ui/src/filters-v2/components/SecondaryFilterContent';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const SecondaryFiltersMenu = ({
  closeMenu,
  feature,
  focusArea,
  setSecondaryFilters,
  secondaryFilterDefinitions,
  stagingSecondaryFilters,
  setStagingSecondaryFilter,
  myObservation,
  selectedSecondaryFilterKey,
  setSelectedSecondaryFilterKey,
  analysisName,
  dashboardGlobalFilters,
}: {
  closeMenu: () => void;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  setSecondaryFilters: (params: {
    selections: SecondaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  stagingSecondaryFilters: SecondaryFilterSelection[];
  setStagingSecondaryFilter: (params: {
    secondaryFilterKey: string;
    value: FilterStoreValue;
  }) => void;
  myObservation: MyObservation | null;
  selectedSecondaryFilterKey: string | null;
  setSelectedSecondaryFilterKey: (key: string | null) => void;
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}) => {
  const language = useLanguage();

  const items: FilterMenuDualPanelItemProps[] = useMemo(() => {
    const allItems: FilterMenuDualPanelItemProps[] = [];

    for (const secondaryFilterDefinition of secondaryFilterDefinitions) {
      allItems.push({
        key: secondaryFilterDefinition.columnHeaderValue,
        translatedContent: language.dangerousUserInput({
          value: secondaryFilterDefinition.displayName,
        }),
        onClick: () => {
          setSelectedSecondaryFilterKey(
            secondaryFilterDefinition.columnHeaderValue,
          );
        },
        selected:
          selectedSecondaryFilterKey ===
          secondaryFilterDefinition.columnHeaderValue,
        itemValue: secondaryFilterDefinition.columnHeaderValue,
        content: (
          <SecondaryFilterContent
            stagingSecondaryFilters={stagingSecondaryFilters}
            feature={feature}
            focusArea={focusArea}
            secondaryFilterKey={secondaryFilterDefinition.columnHeaderValue}
            setStagingSecondaryFilter={setStagingSecondaryFilter}
            secondaryFilterDefinitions={secondaryFilterDefinitions}
            myObservation={myObservation}
            analysisName={analysisName}
            dashboardGlobalFilters={dashboardGlobalFilters}
          />
        ),
      });
    }

    return allItems;
  }, [
    secondaryFilterDefinitions,
    language,
    selectedSecondaryFilterKey,
    stagingSecondaryFilters,
    feature,
    focusArea,
    setStagingSecondaryFilter,
    myObservation,
    analysisName,
    dashboardGlobalFilters,
    setSelectedSecondaryFilterKey,
  ]);

  const invalidKeys = determineSecondaryFilterInvalidKeys({
    stagingSecondaryFilters,
    focusArea,
    myObservation,
    feature,
    analysisName,
  });

  return (
    <FilterMenuDualPanel
      items={items}
      bottomProps={{
        onApply: () => {
          setSecondaryFilters({
            selections: stagingSecondaryFilters,
          });

          closeMenu();
        },
        onCancel: () => {
          closeMenu();
        },
        applyDisabled: invalidKeys.length > 0,
        errorTranslatedContent:
          invalidKeys.length > 0
            ? language.pleaseSelect({
                fieldDisplayNames: invalidKeys.map((invalidKey) =>
                  determineSecondaryFilterDisplayName({
                    secondaryFilterKey: invalidKey,
                    secondaryFilterDefinitions,
                  }),
                ),
              })
            : null,
        appliedCount: determineNonDefaultSecondaryFilterCount({
          stagingSecondaryFilters,
          feature,
          focusArea,
          myObservation,
          analysisName,
        }),
      }}
    />
  );
};
